import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { Box, TextField, TextFieldProps, Typography } from '@mui/material';
import {
  SingleInputDateRangeField,
  SingleInputDateRangeFieldProps,
} from '@mui/x-date-pickers-pro';
import { forwardRef } from 'react';

import { getTimezoneAbbreviation } from 'helpers/datetime';

import { useTimezoneContext } from 'components/timezones';

import type { DateTime } from 'luxon';

type TimezoneTextFieldProps = TextFieldProps & {
  placeholder?: string;
  showCurrentTimeZone?: boolean;
  timezoneProp?: string;
};

const TimezoneTextField = forwardRef<HTMLInputElement, TimezoneTextFieldProps>(
  (
    {
      placeholder,
      showCurrentTimeZone,
      timezoneProp: timezone,
      value,
      ...props
    }: TimezoneTextFieldProps,
    ref,
  ) => {
    const { currentTimeZoneAbbreviation: contextTimezoneAbbr } =
      useTimezoneContext();
    const timezoneAbbreviation = timezone
      ? getTimezoneAbbreviation(timezone)
      : contextTimezoneAbbr;

    const displayValue = placeholder
      ? `${placeholder} ${value ? `(${value})` : ''}`
      : value;

    return (
      <TextField
        {...props}
        inputProps={{
          ...props.inputProps,
          readOnly: true,
          value: displayValue,
        }}
        InputProps={{
          ...props.InputProps,
          endAdornment: (
            <Box alignItems="center" display="flex" gap={0}>
              {showCurrentTimeZone && (
                <Box px={0.5}>
                  <Typography fontStyle="italic" noWrap variant="caption">
                    {timezoneAbbreviation}
                  </Typography>
                </Box>
              )}

              {props.InputProps?.endAdornment}

              <ArrowDropDownIcon />
            </Box>
          ),
        }}
        ref={ref}
        value={displayValue}
      />
    );
  },
);

interface DateRangeFieldProps {
  allowClear?: boolean;
  onClear: () => void;
  placeholder?: string;
  showCurrentTimeZone?: boolean;
  TextFieldProps?: TextFieldProps;
  timezoneProp?: string;
}

type DateRangeFieldComponent = (<TDate>(
  props: SingleInputDateRangeFieldProps<TDate> &
    React.RefAttributes<HTMLInputElement> &
    DateRangeFieldProps,
) => JSX.Element) & { fieldType?: string };

const DateRangeField = forwardRef<
  HTMLInputElement,
  SingleInputDateRangeFieldProps<DateTime> & DateRangeFieldProps
>(
  (
    {
      allowClear,
      onClear,
      placeholder,
      showCurrentTimeZone = true,
      TextFieldProps,
      timezoneProp,
      ...props
    },
    ref,
  ) => (
    <SingleInputDateRangeField<DateTime>
      {...props}
      clearable={allowClear}
      onClear={onClear}
      ref={ref}
      slotProps={{
        textField: {
          placeholder,
          showCurrentTimeZone,
          timezoneProp,
          ...TextFieldProps,
        } as any,
      }}
      slots={{
        textField: TimezoneTextField,
      }}
    />
  ),
) as DateRangeFieldComponent;

DateRangeField.fieldType = 'single-input';

export default DateRangeField;
